import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const csaUnit2Notes: UnitNotes = {
  unitId: "unit-2",
  unitName: "Unit 2: Selection and Iteration",
  examWeight: "25–35%",
  focus: "Algorithms and flow of control using Boolean logic, branching (if-statements), and repetitive execution (loops).",
  sections: [
    {
      id: "topic-2-1",
      title: "Topic 2.1: Algorithms with Selection and Repetition",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧱 Core Algorithm Concepts</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Algorithms are step-by-step procedures to solve problems. In Java, the flow of control is sequential (line by line) unless modified by control structures.
            </p>
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Sequencing</h4>
                <p className="text-sm text-slate-600 mt-1">The default mode where code runs in the order written.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Selection</h4>
                <p className="text-sm text-slate-600 mt-1">Using conditions to decide which code blocks execute (Decision making).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">Repetition</h4>
                <p className="text-sm text-slate-600 mt-1">Using loops to execute code multiple times (Iteration).</p>
              </div>
            </div>
            <ImportantNote>
              <strong>AP Exam Tip:</strong> You will be asked to determine output by combining all three elements. Trace the code exactly as the computer would, line by line.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-2",
      title: "Topic 2.2: Boolean Expressions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Relational Operators</h3>
            <p className="text-slate-700 mb-4">A Boolean expression evaluates to either <code className="font-mono font-bold">true</code> or <code className="font-mono font-bold">false</code>.</p>
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full border-collapse bg-white rounded-lg overflow-hidden border border-slate-200">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-bold text-slate-700">Operator</th>
                    <th className="px-4 py-2 text-left text-sm font-bold text-slate-700">Meaning</th>
                    <th className="px-4 py-2 text-left text-sm font-bold text-slate-700">Usage</th>
                    <th className="px-4 py-2 text-left text-sm font-bold text-slate-700">Example (a=5, b=10)</th>
                  </tr>
                </thead>
                <tbody className="text-sm text-slate-600">
                  <tr className="border-t">
                    <td className="px-4 py-2 font-mono text-blue-600">==</td>
                    <td className="px-4 py-2">Equal to</td>
                    <td className="px-4 py-2 font-mono">a == b</td>
                    <td className="px-4 py-2 font-semibold text-red-600">false</td>
                  </tr>
                  <tr className="border-t">
                    <td className="px-4 py-2 font-mono text-blue-600">!=</td>
                    <td className="px-4 py-2">Not equal to</td>
                    <td className="px-4 py-2 font-mono">a != b</td>
                    <td className="px-4 py-2 font-semibold text-green-600">true</td>
                  </tr>
                  <tr className="border-t">
                    <td className="px-4 py-2 font-mono text-blue-600">&lt;</td>
                    <td className="px-4 py-2">Less than</td>
                    <td className="px-4 py-2 font-mono">a &lt; b</td>
                    <td className="px-4 py-2 font-semibold text-green-600">true</td>
                  </tr>
                  <tr className="border-t">
                    <td className="px-4 py-2 font-mono text-blue-600">&gt;</td>
                    <td className="px-4 py-2">Greater than</td>
                    <td className="px-4 py-2 font-mono">a &gt; b</td>
                    <td className="px-4 py-2 font-semibold text-red-600">false</td>
                  </tr>
                  <tr className="border-t">
                    <td className="px-4 py-2 font-mono text-blue-600">&lt;=</td>
                    <td className="px-4 py-2">Less than or equal</td>
                    <td className="px-4 py-2 font-mono">a &lt;= b</td>
                    <td className="px-4 py-2 font-semibold text-green-600">true</td>
                  </tr>
                  <tr className="border-t">
                    <td className="px-4 py-2 font-mono text-blue-600">&gt;=</td>
                    <td className="px-4 py-2">Greater than or equal</td>
                    <td className="px-4 py-2 font-mono">a &gt;= b</td>
                    <td className="px-4 py-2 font-semibold text-red-600">false</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="space-y-4">
              <KeyTerm 
                term="Primitive Comparison" 
                definition="The == operator compares actual values for types like int, double, and boolean. (e.g., 3 == 3 is true)."
              />
              <KeyTerm 
                term="Reference Comparison" 
                definition="The == operator compares memory addresses (references) for Objects. String s1 = new String('hi'); String s2 = new String('hi'); s1 == s2 evaluates to false."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-3",
      title: "Topic 2.3: if Statements",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚦 One-Way and Two-Way Selection</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-800 p-4 rounded-lg">
                <h4 className="text-slate-300 text-xs font-bold mb-2 uppercase tracking-widest">One-Way</h4>
                <pre className="text-blue-400 font-mono text-sm">
                  {`if (condition) {\n  // only if true\n}`}
                </pre>
              </div>
              <div className="bg-slate-800 p-4 rounded-lg">
                <h4 className="text-slate-300 text-xs font-bold mb-2 uppercase tracking-widest">Two-Way (if-else)</h4>
                <pre className="text-blue-400 font-mono text-sm">
                  {`if (condition) {\n  // true\n} else {\n  // false\n}`}
                </pre>
              </div>
            </div>
            <ImportantNote>
              <strong>Common Pitfall:</strong> Do not put a semicolon <code className="bg-red-100 text-red-700 px-1 rounded">;</code> immediately after the <code className="font-mono">if (condition)</code>. This ends the if-statement immediately, causing the block below it to always execute.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-4",
      title: "Topic 2.4: Nested if Statements",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌲 Multi-Way Selection</h3>
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 mb-4">
              <h4 className="font-bold text-indigo-900 mb-2">Multi-Way Logic (else if)</h4>
              <p className="text-sm text-slate-700 leading-relaxed">
                Used to check mutually exclusive conditions. Once a true condition is found, subsequent <code className="font-mono">else if</code> blocks are skipped.
              </p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h4 className="font-bold text-orange-900 mb-2">The Dangling Else</h4>
              <p className="text-sm text-slate-700 mb-2">
                In Java, an <code className="font-mono">else</code> always belongs to the <strong>closest preceding unmatched</strong> <code className="font-mono">if</code>, regardless of indentation.
              </p>
              <div className="bg-white p-3 rounded border border-orange-100 font-mono text-xs">
                {`if (x > 0)\n    if (y > 0)\n        System.out.println("Both positive");\nelse // Belongs to if (y > 0)`}
              </div>
              <p className="text-xs text-slate-500 mt-2 italic">Fix: Always use curly braces {"{}"} to avoid ambiguity.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-5",
      title: "Topic 2.5: Compound Boolean Expressions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚡ Logical Operators</h3>
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="p-4 rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-mono font-bold text-lg">!</span>
                  <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">NOT</span>
                </div>
                <p className="text-xs text-slate-600 italic">Precedence: High</p>
                <p className="text-xs mt-2">Inverts the value (<InlineMath math="!\text{true} = \text{false}" />)</p>
              </div>
              <div className="p-4 rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-mono font-bold text-lg">&&</span>
                  <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">AND</span>
                </div>
                <p className="text-xs text-slate-600 italic">Precedence: Medium</p>
                <p className="text-xs mt-2">True only if <strong>both</strong> sides are true.</p>
              </div>
              <div className="p-4 rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-mono font-bold text-lg">||</span>
                  <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">OR</span>
                </div>
                <p className="text-xs text-slate-600 italic">Precedence: Low</p>
                <p className="text-xs mt-2">True if <strong>at least one</strong> side is true.</p>
              </div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h4 className="font-bold text-yellow-900">Short-Circuit Evaluation</h4>
              <p className="text-sm text-slate-700 mt-1">Java evaluates left to right and stops early if the result is known:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-slate-700">
                <li><strong>AND (&&):</strong> If left side is <code className="font-mono">false</code>, result is false. Right side is skipped.</li>
                <li><strong>OR (||):</strong> If left side is <code className="font-mono">true</code>, result is true. Right side is skipped.</li>
              </ul>
              <div className="mt-3 p-2 bg-white rounded border border-yellow-100 font-mono text-[11px]">
                {`if (count != 0 && sum / count > 10) // Prevents div by 0`}
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-6",
      title: "Topic 2.6: Comparing Boolean Expressions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📜 De Morgan’s Laws</h3>
            <p className="text-slate-700 mb-4">Rules for distributing the NOT operator over compound expressions:</p>
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 space-y-6">
              <div className="text-center">
                <BlockMath math="!(A \text{ \&\& } B) \equiv !A \text{ || } !B" />
              </div>
              <div className="h-px bg-slate-200 w-full"></div>
              <div className="text-center">
                <BlockMath math="!(A \text{ || } B) \equiv !A \text{ \&\& } !B" />
              </div>
            </div>
            <ImportantNote>
              <strong>Remember:</strong> Flip the logical operator (AND ↔ OR) AND flip relational operators (e.g., <InlineMath math="<" /> becomes <InlineMath math="\ge" />).
            </ImportantNote>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-bold text-blue-900">Object Content Equality</h4>
              <p className="text-sm text-slate-700 mt-1">To check if objects (like Strings) have the same data, use <code className="font-mono">.equals()</code>, not <code className="font-mono">==</code>.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-7",
      title: "Topic 2.7: while Loops",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 while Loop Syntax</h3>
            <div className="bg-slate-800 text-blue-300 p-4 rounded-lg font-mono text-sm mb-4">
              <p><span className="text-pink-400">while</span> (boolean_condition) {'{'}</p>
              <p className="ml-4 text-slate-400">// code to repeat</p>
              <p className="ml-4 text-green-400">// must eventually make condition false!</p>
              <p>{'}'}</p>
            </div>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900 text-sm">Infinite Loop</h4>
                <p className="text-xs text-slate-700 mt-1">Occurs if the condition never becomes false. The program hangs or crashes.</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900 text-sm">Off-by-One Error</h4>
                <p className="text-xs text-slate-700 mt-1">Loop runs one too many or one too few times. Trace your variables manually to catch this.</p>
              </div>
              <KeyTerm 
                term="Input Validation" 
                definition="A common use for while loops: repeatedly prompting a user until they provide valid data."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-8",
      title: "Topic 2.8: for Loops",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔢 for Loop Structure</h3>
            <div className="bg-slate-800 text-slate-100 p-4 rounded-lg font-mono text-xs overflow-x-auto mb-4">
              <p><span className="text-pink-400">for</span> (<span className="text-blue-400">int</span> i = <span className="text-orange-400">0</span>; i &lt; <span className="text-orange-400">10</span>; i++) {'{'}</p>
              <p className="ml-4">{"// Loop body"}</p>
              <p>{'}'}</p>
            </div>
            <h4 className="font-bold text-slate-800 text-sm mb-2">Execution Order:</h4>
            <ol className="list-decimal pl-6 space-y-2 text-sm text-slate-700 mb-4">
              <li><strong>Initialization:</strong> Runs once at the start (<code className="font-mono">int i = 0</code>).</li>
              <li><strong>Condition:</strong> Checked <em>before</em> every iteration. If false, loop ends.</li>
              <li><strong>Body:</strong> The code inside the curly braces executes.</li>
              <li><strong>Update:</strong> Runs <em>after</em> the body (<code className="font-mono">i++</code>).</li>
              <li><strong>Repeat:</strong> Goes back to Step 2.</li>
            </ol>
            <KeyTerm 
              term="Loop Scope" 
              definition="Variables declared in the for-loop header (like 'i') only exist within that loop."
            />
          </section>
        </div>
      )
    },
    {
      id: "topic-2-9",
      title: "Topic 2.9: Implementing Selection and Iteration Algorithms",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚙️ Standard Algorithms</h3>
            <div className="space-y-4">
              <div className="p-4 rounded-lg border border-slate-200 bg-white">
                <h4 className="font-bold text-slate-900 text-sm">1. Divisibility</h4>
                <p className="text-xs text-slate-600 mt-1 mb-2">Use the modulo operator inside a loop or if-statement.</p>
                <code className="bg-slate-100 px-2 py-1 rounded text-xs font-mono">if (num % 2 == 0) // check for even</code>
              </div>
              <div className="p-4 rounded-lg border border-slate-200 bg-white">
                <h4 className="font-bold text-slate-900 text-sm">2. Digit Extraction</h4>
                <p className="text-xs text-slate-600 mt-1 mb-2">Processing an integer digit by digit using <InlineMath math="\% 10" /> and <InlineMath math="/ 10" />.</p>
                <div className="bg-slate-50 p-2 rounded font-mono text-[11px]">
                  {`while (num > 0) {\n  int digit = num % 10; // Get last\n  num /= 10; // Remove last\n}`}
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="p-4 rounded-lg border border-slate-200 bg-white">
                  <h4 className="font-bold text-slate-900 text-sm">3. Finding Min/Max</h4>
                  <p className="text-xs text-slate-600 mt-1 italic">Initialize to the first element or Integer.MAX_VALUE/MIN_VALUE.</p>
                </div>
                <div className="p-4 rounded-lg border border-slate-200 bg-white">
                  <h4 className="font-bold text-slate-900 text-sm">4. Accumulation</h4>
                  <p className="text-xs text-slate-600 mt-1 italic">Initialize sum to 0 before the loop and add inside.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-10",
      title: "Topic 2.10: Implementing String Algorithms",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧵 Java String Methods</h3>
            <p className="text-slate-700 text-sm mb-4">Recall that Strings are <strong>immutable</strong> (methods return *new* strings).</p>
            <div className="grid md:grid-cols-2 gap-2 mb-4">
              <div className="p-2 border rounded bg-slate-50 font-mono text-xs">str.length()</div>
              <div className="p-2 border rounded bg-slate-50 font-mono text-xs">str.substring(index)</div>
              <div className="p-2 border rounded bg-slate-50 font-mono text-xs">str.substring(start, end)</div>
              <div className="p-2 border rounded bg-slate-50 font-mono text-xs">str.indexOf("text")</div>
            </div>
            <div className="bg-slate-800 text-pink-300 p-4 rounded-lg font-mono text-sm mb-4">
              <p className="text-slate-400">// Standard String Traversal</p>
              <p><span className="text-pink-400">for</span> (<span className="text-blue-400">int</span> i = <span className="text-orange-400">0</span>; i &lt; str.length(); i++) {'{'}</p>
              <p className="ml-4 text-slate-100">String letter = str.substring(i, i + <span className="text-orange-400">1</span>);</p>
              <p>{'}'}</p>
            </div>
            <ImportantNote>
              <strong>StringIndexOutOfBoundsException:</strong> Thrown if you try to access an index <InlineMath math="< 0" /> or <InlineMath math="\ge \text{length()}" />.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-11",
      title: "Topic 2.11: Nested Iteration",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🪆 Logic of Nested Loops</h3>
            <p className="text-slate-700 text-sm leading-relaxed mb-4">
              The <strong>inner loop</strong> runs to completion for <strong>every single iteration</strong> of the outer loop.
            </p>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
              <h4 className="font-bold text-purple-900 text-center">Total Iterations Formula</h4>
              <div className="mt-2 text-center">
                <BlockMath math="\text{Iterations} = \text{Outer Count} \times \text{Inner Count}" />
              </div>
            </div>
            <div className="bg-slate-800 text-white p-4 rounded-lg font-mono text-xs mb-4">
              <p><span className="text-pink-400">for</span> (int row = 0; row &lt; 3; row++) {'{'}</p>
              <p className="ml-4"><span className="text-pink-400">for</span> (int col = 0; col &lt; 5; col++) {'{'}</p>
              <p className="ml-8">System.out.print("*");</p>
              <p className="ml-4">{'}'}</p>
              <p className="ml-4">System.out.println(); <span className="text-slate-400">// New line after each row</span></p>
              <p>{'}'}</p>
            </div>
            <Mnemonic title="Tracing Tips" content="Use a trace table with columns for the outer variable, inner variable, and the current state of output." />
          </section>
        </div>
      )
    },
    {
      id: "topic-2-12",
      title: "Topic 2.12: Informal Run-Time Analysis",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⏱️ Execution Counting</h3>
            <p className="text-slate-700 text-sm mb-4">Analyzing how many times a statement executes to determine efficiency.</p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Simple Loop</h4>
                <p className="text-xs text-slate-700 mt-1 italic">Running 0 to N: executes N times.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Independent Nested Loops</h4>
                <p className="text-xs text-slate-700 mt-1 italic">Efficiency is N × M.</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">Dependent Nested Loops</h4>
                <p className="text-xs text-slate-700 mt-1 mb-2">Example: Inner loop condition is <code className="font-mono">j &lt; i</code>.</p>
                <div className="text-center font-bold">
                  <InlineMath math="\text{Count} \approx \frac{N^2}{2}" />
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-4 italic">
              *Note: AP CSA Exam focuses on counting exact execution passes, not formal Big O notation.
            </p>
          </section>
        </div>
      )
    }
  ]
};

export default csaUnit2Notes;